/* eslint-disable indent */
import React, { FC, useCallback, useContext, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { captureException } from "@sentry/nextjs";
import { ImageViewer } from "components/molecules/ImageViewer";
import { LightboxWithMetadata } from "components/molecules/Lightbox";
import SessionToolbar from "components/molecules/SessionToolbar";
import { createSession, deleteImage, editImage, getSessionByName, getSessionImages, getSessionImagesById, getSessions, onToggleVisibility, poll, pollEditImage, streamAudioData, voteOnImage } from "lib/actions";
import { AppContext } from "lib/contexts";
import { AppReducerActionType, ImageDTO, Option, SessionDTO, UserPremiumType } from "types";
import * as Styled from "./SessionRecorder.styled";
interface SessionRecorderProps {
  session?: SessionDTO;
}
export const SessionRecorder: FC<SessionRecorderProps> = ({
  session
}: SessionRecorderProps) => {
  const {
    state,
    dispatch
  } = useContext(AppContext);
  const [isRecording, setIsRecording] = useState(false);
  const silenceThreshold = 2;
  const audioContextRef = useRef<AudioContext | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const scriptProcessorRef = useRef<ScriptProcessorNode | null>(null);
  const audioBufferRef = useRef<Float32Array[]>([]); // Store temporary audio data
  const silenceDurationRef = useRef(0); // Silence duration tracker
  const activeDurationRef = useRef(0); // Active sound duration tracker
  const [sessions, setSessions] = useState<Option[]>([]); // List of session names
  const [transcriptionName, setTranscriptionName] = useState<string>(session?.name || "");
  const [imageDTOs, setImageDTOs] = useState<ImageDTO[]>([]); // Track the list of images
  const [selectedImage, setSelectedImage] = useState<ImageDTO | null>(null);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(session?.id || null);
  const [totalCost, setTotalCost] = useState(Math.round(session?.sessionCost.totalCost ?? 0));
  const [sessionTerminated, setSessionTerminated] = useState(false);
  const isOwner = state.username === session?.username || !session?.username;
  const [isFirstLoad, setIsFirstLoad] = useState(true); // Track if session is first loaded

  const loadImages = async (sessionName: string) => {
    try {
      const newImages = isOwner ? await getSessionImages(sessionName) : await getSessionImagesById(session?.id as string);
      if (newImages.image && newImages.image.length > 0) {
        setImageDTOs(prevImages => {
          // Merge new images with existing ones, avoid duplicates
          const mergedImages = newImages.image?.filter(newImg => !prevImages.some(img => img.id === newImg.id)) || [];

          // Add new images at the front of the list
          return [...mergedImages, ...prevImages];
        });
      }
    } catch (error) {
      console.error("Error fetching images:", error);
    }
  };
  useEffect(() => {
    const fetchSessions = async () => {
      try {
        const response = await getSessions();
        // Check if response.sessionNames exists and is an array
        if (response && response.sessionNames) {
          const sessionOptions = response.sessionNames.map(session => ({
            label: session,
            value: session
          }));
          setSessions(sessionOptions);
        }
      } catch (error) {
        console.error("Error fetching sessions:", error);
      }
    };
    fetchSessions();
  }, []);
  useEffect(() => {
    if (session) {
      setTranscriptionName(session.name);
      setSessionId(session.id);
      setTotalCost(Math.round(session.sessionCost.totalCost));
      loadImages(session.name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, sessionId, transcriptionName]);
  useEffect(() => {
    if (!transcriptionName || !isRecording) return;
    const pollImages = async () => {
      await loadImages(transcriptionName);
    };
    if (isFirstLoad) {
      // Load images once when session is first entered
      pollImages();
      setIsFirstLoad(false);
    }
    const interval = setInterval(pollImages, 3000); // Poll every 10 seconds when recording is on

    return () => clearInterval(interval); // Cleanup interval on component unmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transcriptionName, isRecording, isFirstLoad]);
  const encodeWAV = (samples: Float32Array, sampleRate = 44100) => {
    const buffer = new ArrayBuffer(44 + samples.length * 2);
    const view = new DataView(buffer);
    const writeString = (view: DataView, offset: number, str: string) => {
      for (let i = 0; i < str.length; i++) {
        view.setUint8(offset + i, str.charCodeAt(i));
      }
    };
    const floatTo16BitPCM = (output: DataView, offset: number, input: Float32Array) => {
      for (let i = 0; i < input.length; i++, offset += 2) {
        const s = Math.max(-1, Math.min(1, input[i]));
        output.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
      }
    };

    // RIFF chunk descriptor
    writeString(view, 0, "RIFF");
    view.setUint32(4, 36 + samples.length * 2, true);
    writeString(view, 8, "WAVE");

    // FMT sub-chunk
    writeString(view, 12, "fmt ");
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true); // Mono audio
    view.setUint16(22, 1, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * 2, true);
    view.setUint16(32, 2, true);
    view.setUint16(34, 16, true);

    // Data sub-chunk
    writeString(view, 36, "data");
    view.setUint32(40, samples.length * 2, true);
    floatTo16BitPCM(view, 44, samples);
    return buffer;
  };
  const sendAudioChunk = async (samples: Float32Array, sampleRate: number) => {
    const wavData = encodeWAV(samples, sampleRate); // Encode to WAV format
    const audioBlob = new Blob([wavData], {
      type: "audio/wav"
    });
    const response = await streamAudioData(audioBlob, transcriptionName);
    if (response === null || response.error === "Payment Required") {
      setIsRecording(false);
      setSessionTerminated(true);
      toast.error("Session terminated due to insufficient funds!", {
        position: "bottom-center"
      });
      stopRecording();
      return;
    }
    if (response.sessionCost) {
      if (response.sessionCost.totalCost !== undefined) {
        setTotalCost(Math.round(response.sessionCost.totalCost));
      }
    }
  };
  const startRecording = async () => {
    if (!transcriptionName) {
      toast.error("Please enter a transcription name.", {
        position: "bottom-center"
      });
      return;
    }
    if (sessionTerminated || state.gold < 1) {
      navigateToTopUpPage();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true
      });
      const audioContext = new AudioContext();
      const sampleRate = audioContext.sampleRate;
      const source = audioContext.createMediaStreamSource(stream);
      const scriptProcessor = audioContext.createScriptProcessor(4096, 1, 1);
      scriptProcessor.onaudioprocess = audioProcessingEvent => {
        const inputBuffer = audioProcessingEvent.inputBuffer;
        const inputData = inputBuffer.getChannelData(0);
        audioBufferRef.current.push(new Float32Array(inputData)); // Add audio data to buffer

        // Calculate the average amplitude to help visualize whether sound is detected
        const averageAmplitude = inputData.reduce((acc, sample) => acc + Math.abs(sample), 0) / inputData.length;
        const backgroundNoiseThreshold = 0.02; // Tolerate low-level background noise
        const isSilent = averageAmplitude < backgroundNoiseThreshold;
        if (isSilent) {
          silenceDurationRef.current += audioProcessingEvent.inputBuffer.duration;
        } else {
          silenceDurationRef.current = 0;
          activeDurationRef.current += audioProcessingEvent.inputBuffer.duration;
        }

        // If the total active sound duration exceeds 5 seconds OR silence exceeds 1 second, send the chunk
        if (silenceDurationRef.current >= silenceThreshold) {
          const allSamples = concatenateFloat32Array(audioBufferRef.current);
          sendAudioChunk(allSamples, sampleRate); // Send the entire chunk of speech
          audioBufferRef.current = []; // Clear buffer after sending
          activeDurationRef.current = 0; // Reset active duration
          silenceDurationRef.current = 0; // Reset silence duration
        }
      };
      audioContextRef.current = audioContext;
      mediaStreamRef.current = stream;
      scriptProcessorRef.current = scriptProcessor;
      source.connect(scriptProcessor);
      scriptProcessor.connect(audioContext.destination);
      setIsRecording(true);
    } catch (error) {
      console.error("Error accessing microphone:", error);
    }
  };
  const stopRecording = () => {
    if (audioContextRef.current) {
      audioContextRef.current.close();
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
    }
    if (scriptProcessorRef.current) {
      scriptProcessorRef.current.disconnect();
    }
    setIsRecording(false);
  };
  const concatenateFloat32Array = (arrays: Float32Array[]) => {
    let totalLength = arrays.reduce((sum, arr) => sum + arr.length, 0);
    let result = new Float32Array(totalLength);
    let offset = 0;
    arrays.forEach(array => {
      result.set(array, offset);
      offset += array.length;
    });
    return result;
  };
  const handleImageClick = (image: ImageDTO) => {
    setSelectedImage(image);
    setIsLightboxOpen(true);
  };
  const handleLightboxClose = () => {
    setIsLightboxOpen(false);
    setSelectedImage(null);
    dispatch({
      type: AppReducerActionType.TOGGLE_OVERLAY_VISIBILITY,
      payload: {
        ...state,
        isOverlayVisible: false
      }
    });
  };
  const handleToggle = (imageId: string) => {
    const currentVisibility = imageDTOs.find(img => img.id === imageId)?.public;
    const newVisibility = !currentVisibility;
    onToggleVisibility(imageId, newVisibility).then(() => {
      setImageDTOs(prev => prev.map(image => image.id === imageId ? {
        ...image,
        public: newVisibility
      } : image));
    }).catch(error => {
      captureException("Failed to update visibility status.", error);
    });
  };
  const handleVote = async (imageId: string) => {
    const currentImage = imageDTOs.find(img => img.id === imageId);
    if (!currentImage) return;
    const newVoteStatus = !currentImage.votedByUser;
    try {
      await voteOnImage(imageId, newVoteStatus);
      setImageDTOs(prev => prev.map(img => img.id === imageId ? {
        ...img,
        votedByUser: newVoteStatus,
        voteCount: img.voteCount + (newVoteStatus ? 1 : -1)
      } : img));
      if (selectedImage?.id === imageId) {
        setSelectedImage(prev => {
          if (!prev) return null;
          return {
            ...prev,
            votedByUser: newVoteStatus,
            voteCount: prev.voteCount + (newVoteStatus ? 1 : -1)
          };
        });
      }
    } catch (error: any) {
      captureException("Failed to update vote status.", error);
    }
  };
  const updateImage = (updatedImage: ImageDTO) => {
    setImageDTOs(prevImages => {
      return prevImages.map(image => image.id === updatedImage.id ? updatedImage : image);
    });
  };
  const navigateToTopUpPage = () => {
    if (state.userPremiumLevel === UserPremiumType.NONE) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      return;
    } else {
      dispatch({
        type: AppReducerActionType.SHOW_PRICING_TABLE,
        payload: {
          ...state,
          showPricingTable: true
        }
      });
      return;
    }
  };
  const handleEditSubmit = useCallback(async (editRequest: string, isPremium: boolean, imageId: string, editType: string, goldCost: number, negativePrompt?: string, cfgScale?: number, numDetections?: number, urlOrFile?: string | File, base64Image?: string, creativity?: number, model?: string) => {
    if (!state.isLoggedIn) {
      dispatch({
        type: AppReducerActionType.SHOW_AUTH_MODAL,
        payload: {
          ...state,
          showAuthModal: true
        }
      });
      return;
    }
    if (state.userPremiumLevel === UserPremiumType.NONE && state.gold < goldCost) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      return;
    } else if (state.gold < goldCost) {
      dispatch({
        type: AppReducerActionType.SHOW_PRICING_TABLE,
        payload: {
          ...state,
          showPricingTable: true
        }
      });
      return;
    }
    const formData = new FormData();
    formData.append("editRequest", editRequest);
    formData.append("imageId", imageId);
    formData.append("premium", isPremium.toString());
    formData.append("editType", editType);
    if (negativePrompt) {
      formData.append("negativePrompt", negativePrompt);
    }
    if (cfgScale) {
      formData.append("cfgScale", cfgScale.toString());
    }
    if (numDetections) {
      formData.append("numDetections", numDetections.toString());
    }
    if (creativity) {
      formData.append("creativity", creativity.toString());
    }
    if (model) {
      formData.append("model", model);
    }
    if (urlOrFile) {
      if (typeof urlOrFile === "string") {
        formData.append("faceImageUrl", urlOrFile);
      } else {
        formData.append("faceImageFile", urlOrFile);
      }
    }
    if (base64Image) {
      formData.append("base64Image", base64Image);
    }
    const {
      requestId
    } = await editImage(formData);
    if (requestId) {
      const {
        image
      } = await poll(async () => await pollEditImage(requestId), ({
        image
      }) => !image || image.length === 0, 5000);
      let imagesToSet: ImageDTO[] = [];

      // Ensure `images` is an array
      if (Array.isArray(image)) {
        image.forEach((element: ImageDTO) => {
          if (element.parentImage) {
            imagesToSet.push(element.parentImage);
          }
          imagesToSet.push(element);
        });
      } else if (image) {
        if (image.parentImage) {
          imagesToSet.push(image.parentImage);
        }
        imagesToSet.push(image);
      }
      handleSetImages(imagesToSet);
      if (image && image.length > 0) {
        dispatch({
          type: AppReducerActionType.SET_GOLD,
          payload: {
            ...state,
            gold: state.gold - goldCost
          }
        });
      }
    }
    setIsLightboxOpen(false);
  }, [dispatch, state]);
  const handleSetImages = (response: ImageDTO[]) => {
    try {
      if (Array.isArray(response)) {
        setImageDTOs(response);
      } else {
        setImageDTOs([response]);
      }
    } catch (error: any) {
      captureException("Error parsing response:", error);
    }
  };
  const handleConfirmDelete = async (imageId?: string) => {
    if (!imageId) return;
    try {
      let newImageDtos;
      if (imageId) {
        await deleteImage(imageId);
        newImageDtos = imageDTOs.filter(img => img.id !== imageId);
      }
      if (!newImageDtos) return;
      setImageDTOs(newImageDtos);
    } catch (error: any) {
      captureException("Failed to delete image", error);
    }
  };
  const handleIndexChange = (newIndex: number) => {
    setSelectedImage(imageDTOs[newIndex]);
  };
  const handleSessionChange = async (newValue: Option | null) => {
    if (newValue) {
      setImageDTOs([]);
      setTranscriptionName(newValue.value);

      // Check if the session already exists
      const existingSession = sessions.find(session => session.value === newValue.value);
      if (existingSession) {
        // Existing session, fetch sessionId from backend
        try {
          const sessionInfo = await getSessionByName(newValue.value);
          if (sessionInfo.session) {
            setSessionId(sessionInfo.session.id);
            setTotalCost(Math.round(sessionInfo.session.sessionCost.totalCost));
            stopRecording();
          }
          loadImages(newValue.value);
        } catch (error) {
          console.error("Error fetching session:", error);
        }
      } else {
        // New session, create it on the backend
        try {
          const newSession = await createSession(newValue.value);
          if (newSession.session) {
            setSessionId(newSession.session.id);
            setTotalCost(Math.round(newSession.session.sessionCost.totalCost));
            stopRecording();
          }
        } catch (error) {
          console.error("Error creating session:", error);
        }
      }
    } else {
      setImageDTOs([]);
      setTranscriptionName("");
      setSessionId(null);
      setTotalCost(0);
      stopRecording();
    }
  };
  return <Styled.SessionRecorderContainer data-sentry-element="unknown" data-sentry-component="SessionRecorder" data-sentry-source-file="index.tsx">
            <SessionToolbar isOwner={isOwner} isRecording={isRecording} stopRecording={stopRecording} startRecording={startRecording} sessionId={sessionId} sessions={sessions} handleSessionChange={handleSessionChange} transcriptionName={transcriptionName} totalCost={totalCost} disableSessionSelection={session != null} // Disable session selection based on the route
    data-sentry-element="SessionToolbar" data-sentry-source-file="index.tsx" />
            {!isOwner && transcriptionName && <Styled.SessionInfoText>
                    You are viewing session: {transcriptionName}
                </Styled.SessionInfoText>}

            <Styled.ImageViewerContainer $hasImages={imageDTOs.length > 0} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                <ImageViewer imageDtos={imageDTOs} onImageClick={handleImageClick} onVote={handleVote} updateImage={updateImage} onSubmitEdit={handleEditSubmit} data-sentry-element="ImageViewer" data-sentry-source-file="index.tsx" />
            </Styled.ImageViewerContainer>

            {selectedImage && <LightboxWithMetadata imageDtos={imageDTOs} initialImageId={selectedImage.id} onClose={handleLightboxClose} open={isLightboxOpen} onToggleVisibility={handleToggle} onVote={handleVote} onIndexChange={handleIndexChange} handleConfirmDelete={handleConfirmDelete} />}
        </Styled.SessionRecorderContainer>;
};